import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { punchApi, type PunchItem, type PunchListResponse } from '@/lib/apiClient';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { Button } from '@/components';
import {
  STAGE_LABELS, STATUS_LABELS, locationLabel, shortDate, tradeName, sheetGroups, sheetContacts, sheetEmail, joinEmails,
  plural, beforePhotoUrl, type SheetGroup,
} from './punchModel';
import { usePrintPreview, readyToPrint } from './usePrintPreview';

type Data = PunchListResponse & { list: NonNullable<PunchListResponse['list']> };

/**
 * Sub sheets (#518, design "Export per subcontractor"): the items each
 * assignee has to fix — New, Open and Rejected, by room — as printable sheets,
 * one per assignee, each on its own page.
 *
 * "Save as PDF" is the browser's print dialog (the way the PPR and Manpower
 * print). The sheets are rendered into a portal on <body> so the print rules
 * can hide everything else and print only them.
 *
 * Printing is also how a New item reaches its sub, but a print dialog can't
 * say whether it was printed or cancelled. So nothing is marked sent by the
 * print itself: afterwards the sheet asks, and "Mark as Sent" is the step
 * that moves those New items to Open with the ball to the sub.
 */
export default function PunchSheets({ projectId, data, jobNumber, jobTitle, canWork, initial, onClose, onSent }: {
  projectId: number;
  data: Data;
  jobNumber: string;
  jobTitle: string;
  canWork: boolean;
  /** An assignee key to open on, or 'all'. */
  initial: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const groups = useMemo(() => sheetGroups(data.items), [data.items]);
  const [selected, setSelectedRaw] = useState(initial);
  const shown = useMemo(
    () => (selected === 'all' ? groups : groups.filter((g) => g.key === selected)),
    [groups, selected],
  );
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const newCount = shown.reduce((n, g) => n + g.newCount, 0);
  // While a print is being prepared, the preview shows the sheets as they were
  // when Print was pressed: a refresh of the list in that gap must not change
  // what prints apart from what gets marked sent (Codex on #567).
  const [frozen, setFrozen] = useState<SheetGroup[] | null>(null);
  const sheets = frozen ?? shown;

  const subsQ = useQuery({ queryKey: ['punch', projectId, 'subs'], queryFn: () => punchApi.subs(projectId), staleTime: 60_000 });
  const jobLeaders = subsQ.data?.fieldLeaders ?? [];

  const graph = useGraphToken();
  const photoUrls = useMemo(
    () => shown.flatMap((g) => g.items.map(beforePhotoUrl).filter((u): u is string => !!u)),
    [shown],
  );
  const { thumbs, settled, reset } = useThumbnailCache(photoUrls, graph.silent);
  const loading = photoUrls.some((u) => !settled(u));

  // After a print: offer to mark the New items sent. null = not asked.
  // The New items on the sheets as printed, per assignee: only these are
  // marked sent, not whatever is New for them by the time someone confirms.
  const [ask, setAsk] = useState<{ groups: PrintedNew[] } | null>(null);
  const [sending, setSending] = useState(false);
  const askCount = ask ? ask.groups.reduce((n, g) => n + g.itemIds.length, 0) : 0;
  const [result, setResult] = useState<{ ok: string | null; error: string | null }>({ ok: null, error: null });

  const previewRef = useRef<HTMLDivElement>(null);
  const [preparing, setPreparing] = useState(false);
  const { closeRef, mounted } = usePrintPreview(onClose, sending || preparing);

  const print = async () => {
    copySeq.current++;
    setResult({ ok: null, error: null });
    setCopied(null);
    // What will print, taken now: the picker, Print and Back are locked until
    // the dialog has opened, so the sheets can't change while the images
    // finish loading (Codex on #567).
    const printing = shown;
    setFrozen(printing);
    const snapshot = newOnSheets(printing);
    setPreparing(true);
    try {
      if (!(await readyToPrint(previewRef.current, photoUrls, thumbs, graph.needsSignIn, mounted))) return;
      window.print();
    } finally {
      if (mounted.current) { setPreparing(false); setFrozen(null); }
    }
    if (!mounted.current || !canWork || snapshot.length === 0) return;
    // Added to any sheet still waiting to be marked sent, not replacing it:
    // printing a second sub first must not drop the first sub's items (Codex
    // on #567).
    setAsk((prev) => ({ groups: mergePrinted(prev?.groups ?? [], snapshot) }));
  };

  // Email text (design "Copy Email Text"): the sheets' items as plain-text
  // drafts to paste into Outlook — the Project Tracker has no sub emails, so
  // nothing is sent from here. Pasting it is how the items go out, so the
  // same "mark as sent" question follows, for what was copied.
  const [copied, setCopied] = useState<{ text: string; subject: string | null } | null>(null);
  // Another sheet picked: what was copied no longer matches what's shown.
  // Bumped by every copy, pick and print: a copy still waiting on the
  // clipboard when something else happens mustn't bring back its note or its
  // mark-as-sent question afterwards (Codex on #570).
  const copySeq = useRef(0);
  const setSelected = (key: string) => { copySeq.current++; setSelectedRaw(key); setCopied(null); };
  const copyEmail = async () => {
    const seq = ++copySeq.current;
    setResult({ ok: null, error: null });
    setCopied(null);
    const copying = shown;
    const emails = copying.map((g) => sheetEmail(g, {
      jobNumber, jobTitle, contacts: sheetContacts(g.items, jobLeaders),
      tradeLabel: (k) => tradeName(data.trades, k),
      reasonLabel: (k) => data.rejectReasons.find((r) => r.key === k)?.label ?? k,
    }));
    const text = joinEmails(emails);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      if (seq !== copySeq.current) return;
      setResult({ ok: null, error: 'Couldn’t copy to the clipboard. Your browser may be blocking it.' });
      return;
    }
    if (seq !== copySeq.current) return;
    setCopied(copying.length > 1
      ? { text: `Copied ${copying.length} drafts. Paste them into new Outlook emails, one per sub.`, subject: null }
      : { text: 'Copied the email. Paste it into a new Outlook email.', subject: emails[0].subject });
    const snapshot = newOnSheets(copying);
    if (canWork && snapshot.length > 0) setAsk((prev) => ({ groups: mergePrinted(prev?.groups ?? [], snapshot) }));
  };

  const markSent = async () => {
    if (!ask) return;
    setSending(true);
    let sent = 0;
    const failed: string[] = [];
    const done = new Map<string, Set<number>>();
    // One call per assignee. Each only moves that assignee's items that are
    // still New, so running it again after a lost reply changes nothing.
    for (const g of ask.groups) {
      try {
        sent += (await punchApi.sendSheet(projectId, g.assignee, g.itemIds)).sent;
        done.set(g.key, new Set(g.itemIds));
      } catch {
        failed.push(g.label);
      }
    }
    setSending(false);
    // Drop only what was just sent, from the question as it is NOW: a print
    // that finished meanwhile added its own items, and they must stay to be
    // marked (Codex on #567).
    setAsk((prev) => {
      const left = (prev?.groups ?? [])
        .map((g) => ({ ...g, itemIds: g.itemIds.filter((id) => !done.get(g.key)?.has(id)) }))
        .filter((g) => g.itemIds.length > 0);
      return left.length ? { groups: left } : null;
    });
    setResult({
      ok: sent ? `${plural(sent, 'item')} marked sent.` : null,
      error: failed.length ? `Not marked sent for ${failed.join(', ')}. Try again.` : null,
    });
    onSent();
  };

  const printed = shortDate(new Date().toISOString(), { year: true });
  const label = selected === 'all'
    ? `Print All (${shown.length} Sheet${shown.length === 1 ? '' : 's'})`
    : 'Print / Save as PDF';

  return createPortal(
    <div className="pl-sheets" role="dialog" aria-modal="true" aria-label="Sub sheets">
      <div className="pl-sheets-bar">
        <button ref={closeRef} type="button" className="pl-link" onClick={onClose} disabled={sending || preparing}>← Back to the list</button>
        <strong>Sub sheets</strong>
        <span className="pl-spacer" />
        {graph.needsSignIn && photoUrls.length > 0 && (
          <Button variant="secondary" size="sm" onClick={async () => { try { await graph.interactive(); reset(); } catch { /* shown on next try */ } }}>
            Show photos
          </Button>
        )}
        {loading && !graph.needsSignIn && <span className="pl-muted">Loading photos…</span>}
        <Button variant="secondary" onClick={() => { void copyEmail(); }}
          disabled={shown.length === 0 || preparing || sending || subsQ.isPending}>
          {selected === 'all' && shown.length > 1 ? `Copy Email Drafts (${shown.length})` : 'Copy Email Text'}
        </Button>
        <Button variant="primary" onClick={() => { void print(); }} disabled={shown.length === 0 || preparing || sending || subsQ.isPending || (loading && !graph.needsSignIn)}
          title={loading && !graph.needsSignIn ? 'Waiting for the photos to load' : undefined}>
          {label}
        </Button>
      </div>

      <div className="pl-sheets-body">
        <aside className="pl-sheets-side">
          <div className="pl-sheets-pick" role="listbox" aria-label="Whose sheet">
            <SubPick label="All subcontractors" count={total} active={selected === 'all'} disabled={preparing}
              onClick={() => setSelected('all')} />
            {groups.map((g) => (
              <SubPick key={g.key} label={g.label} count={g.items.length} active={selected === g.key} disabled={preparing}
                onClick={() => setSelected(g.key)} />
            ))}
          </div>

          <div className="pl-card pl-sheets-info">
            <div className="pl-caption">Includes</div>
            <p>New, Open and Rejected items, with the before photo, room, notes and due date. Sorted by room.</p>
          </div>

          {selected === 'all' && groups.length > 1 && (
            <p className="pl-muted" style={{ margin: 0 }}>One sheet per sub, each starting on a new page.</p>
          )}
          {canWork && newCount > 0 && !ask && (
            <p className="pl-muted" style={{ margin: 0 }}>
              {newCount} New item{newCount === 1 ? '' : 's'} haven’t gone to {selected === 'all' ? 'their subs' : shown[0]?.label} yet.
              After printing you can mark {newCount === 1 ? 'it' : 'them'} sent.
            </p>
          )}

          {ask && (
            <div className="pl-note pl-form" role="status">
              <span>
                Did it go out? Mark {plural(askCount, 'New item')} as sent to{' '}
                {ask.groups.map((g) => g.label).join(', ')}. The ball moves to {ask.groups.length === 1 ? 'them' : 'each sub'}.
              </span>
              <div className="pl-row">
                <Button variant="primary" size="sm" isLoading={sending} disabled={preparing} onClick={markSent}>Mark as Sent</Button>
                <Button variant="secondary" size="sm" disabled={sending || preparing} onClick={() => setAsk(null)}>Not Yet</Button>
              </div>
            </div>
          )}
          {subsQ.isError && (
            // Printing still works; a sheet whose items name no field leader
            // then says "your E&W contact" (Codex on #567).
            <div className="pl-error" role="alert">
              Couldn’t load this job’s field leaders, so a sheet with none on its items won’t name one.
            </div>
          )}
          {copied && (
            <div className="pl-note pl-form" role="status">
              <span>{copied.text}</span>
              {copied.subject && (
                <span>
                  Subject: <strong>{copied.subject}</strong>{' '}
                  <button type="button" className="pl-link"
                    onClick={() => { void navigator.clipboard?.writeText(copied.subject!).catch(() => undefined); }}>
                    Copy subject
                  </button>
                </span>
              )}
            </div>
          )}
          {result.ok && <div className="pl-note" role="status">{result.ok}</div>}
          {result.error && <div className="pl-error" role="alert">{result.error}</div>}
        </aside>

        <div className="pl-sheets-preview" ref={previewRef}>
          {sheets.length === 0 && (
            <p className="pl-muted">Nothing to send: no New, Open or Rejected items are assigned to anyone.</p>
          )}
          {sheets.map((g, n) => (
            <Sheet key={g.key} group={g} data={data} jobNumber={jobNumber} jobTitle={jobTitle} printed={printed}
              contacts={sheetContacts(g.items, jobLeaders)} thumbs={thumbs} index={n + 1} of={sheets.length} />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** The New items one assignee's printed sheet showed. */
interface PrintedNew { key: string; label: string; assignee: SheetGroup['assignee']; itemIds: number[] }

/** The New items on these sheets, per assignee: what a print or a copy sends. */
function newOnSheets(sheets: SheetGroup[]): PrintedNew[] {
  return sheets
    .map((g) => ({ key: g.key, label: g.label, assignee: g.assignee, itemIds: g.items.filter((i) => i.status === 'new').map((i) => i.id) }))
    .filter((g) => g.itemIds.length > 0);
}

/** Pending sends plus a new print's, one entry per assignee, ids unioned. */
function mergePrinted(prev: PrintedNew[], next: PrintedNew[]): PrintedNew[] {
  const out = new Map(prev.map((g) => [g.key, { ...g, itemIds: [...g.itemIds] }]));
  for (const g of next) {
    const had = out.get(g.key);
    if (had) had.itemIds = [...new Set([...had.itemIds, ...g.itemIds])];
    else out.set(g.key, { ...g, itemIds: [...g.itemIds] });
  }
  return [...out.values()];
}

function SubPick({ label, count, active, disabled, onClick }: {
  label: string; count: number; active: boolean; disabled: boolean; onClick: () => void;
}) {
  return (
    <button type="button" role="option" aria-selected={active} disabled={disabled}
      className={`pl-sheets-sub${active ? ' active' : ''}`} onClick={onClick}>
      <span>{label}</span><span className="pl-muted">{count}</span>
    </button>
  );
}

function Sheet({ group, data, jobNumber, jobTitle, printed, contacts, thumbs, index, of }: {
  group: SheetGroup; data: Data; jobNumber: string; jobTitle: string; printed: string;
  contacts: string[]; thumbs: Record<string, string>; index: number; of: number;
}) {
  const n = group.items.length;
  return (
    <article className="pl-sheet" aria-label={`${group.label} sheet`}>
      <header className="pl-sheet-hd">
        <div>
          <div className="pl-sheet-stage">{STAGE_LABELS[data.list.stage]}</div>
          <h2>{group.label}</h2>
          <div className="pl-sheet-meta">
            Job {jobNumber}{jobTitle ? ` · ${jobTitle}` : ''}<br />
            Printed {printed} · {n} item{n === 1 ? '' : 's'}
          </div>
        </div>
        <img src="/ew-logo-horizontal-green.png" alt="East & Westbrook" className="pl-sheet-logo" />
      </header>

      <div className="pl-sheet-note">
        Fix each item by its due date.{' '}
        {contacts.length > 0
          ? <>Text a photo of the finished work with the item number to <strong>{contacts.join(', ')}, Field Leader{contacts.length > 1 ? 's' : ''}</strong>.</>
          : <>Send a photo of the finished work with the item number to your E&amp;W contact.</>}
        {' '}We verify on site before closing.
      </div>

      <div className="pl-sheet-items">
        {group.items.map((i) => (
          <SheetRow key={i.id} item={i} data={data} thumb={thumbOfBefore(i, thumbs)} />
        ))}
      </div>

      <footer className="pl-sheet-ft">
        <span>East &amp; Westbrook Construction · Buckner, KY</span>
        <span>Sheet {index} of {of}</span>
      </footer>
    </article>
  );
}

function SheetRow({ item, data, thumb }: { item: PunchItem; data: Data; thumb: string | null }) {
  const reason = item.status === 'rejected' && item.rejectReason
    ? data.rejectReasons.find((r) => r.key === item.rejectReason)?.label ?? null
    : null;
  const loc = locationLabel(item);
  return (
    <div className="pl-sheet-row">
      {thumb ? <img className="pl-sheet-photo" src={thumb} alt="" /> : <span className="pl-sheet-photo" aria-hidden />}
      <div>
        <div className="pl-sheet-where">{item.label}{loc !== '—' ? ` · ${loc}` : ''}</div>
        <div className="pl-sheet-desc">{item.description}</div>
        {item.notes && <div className="pl-sheet-notes">{item.notes}</div>}
        <div className="pl-sheet-sub">
          {tradeName(data.trades, item.trade)} · {STATUS_LABELS[item.status]}{reason ? ` — ${reason}` : ''}
        </div>
      </div>
      <div className="pl-sheet-due">
        <div>{item.dueDate ? `Due ${shortDate(item.dueDate)}` : 'No due date'}</div>
        <div className="pl-sheet-check"><span aria-hidden>☐</span> Fixed</div>
      </div>
    </div>
  );
}

function thumbOfBefore(i: PunchItem, thumbs: Record<string, string>): string | null {
  const u = beforePhotoUrl(i);
  return u ? thumbs[u] ?? null : null;
}
